import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, Crown, ShieldCheck, Loader2, Check, History } from "lucide-react";
import { api } from "@/lib/api";
import { startCheckout, usePaymentReturn } from "@/lib/checkout";
import { useWalkie, notify } from "./WalkieLayout";

export default function WalkieStore() {
  const { me, reloadMe } = useWalkie();
  const [shop, setShop] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [busy, setBusy] = useState("");
  const loadWallet = () => api.get("/walkie/wallet").then((r) => setWallet(r.data)).catch(() => {});
  const status = usePaymentReturn(() => { reloadMe(); loadWallet(); });
  useEffect(() => { api.get("/walkie/shop").then((r) => setShop(r.data)); loadWallet(); }, []);
  if (!shop) return <div className="py-16 text-center wt-muted animate-pulse">Loading store…</div>;
  const owned = new Set(me.profile.owned || []);
  const buyItem = async (it) => {
    setBusy(it.id);
    try { await api.post("/walkie/shop/buy", { item_id: it.id }); toast.success(`${it.name} unlocked`); await reloadMe(); loadWallet(); } catch (e) { notify.err(e); }
    setBusy("");
  };
  const pay = async (id) => { setBusy(id); await startCheckout(id); setBusy(""); };
  const premiumCoins = async () => {
    setBusy("prem_coins");
    try { await api.post("/walkie/premium/coins"); toast.success("Premium activated"); await reloadMe(); loadWallet(); } catch (e) { notify.err(e); }
    setBusy("");
  };
  return (
    <div className="space-y-6" data-testid="walkie-store">
      <div className="rounded-3xl p-5 bg-gradient-to-br from-[#FFE300]/25 to-[#FF6A00]/10 border border-[#FFE300]/30">
        <div className="text-xs wt-muted">Coin balance</div>
        <div className="font-black text-4xl flex items-center gap-2" data-testid="walkie-store-balance"><Coins className="w-8 h-8 text-[#FFE300]" />{me.coins}</div>
        {wallet && <div className="text-xs wt-muted mt-1">Tips received: {wallet.tips_received} · Tips sent: {wallet.tips_sent}</div>}
        {status === "checking" && <div className="text-xs mt-2 flex items-center gap-1 text-[#00F0FF]" data-testid="walkie-payment-checking"><Loader2 className="w-3 h-3 animate-spin" /> Confirming payment…</div>}
      </div>

      <section className="space-y-2">
        <h2 className="font-black">Buy coins</h2>
        <div className="grid grid-cols-3 gap-2">
          {shop.packages.map((p) => (
            <button key={p.id} onClick={() => pay(p.id)} disabled={!!busy} data-testid={`walkie-pack-${p.id}`} className="wt-card rounded-2xl p-3 text-center hover:border-[#FFE300]/60 transition-colors">
              <Coins className="w-6 h-6 mx-auto text-[#FFE300]" />
              <div className="font-black mt-1">{p.coins.toLocaleString()}</div>
              <div className="text-xs font-bold mt-1 bg-[#FFE300] text-black rounded-full py-1">{busy === p.id ? "…" : `$${p.price.toFixed(2)}`}</div>
            </button>
          ))}
        </div>
      </section>

      <section className="wt-card rounded-2xl p-4 border-[#FF2BD6]/40" data-testid="walkie-premium-card">
        <div className="font-black flex items-center gap-2"><Crown className="w-5 h-5 text-[#FFE300]" /> Walkie Premium</div>
        <ul className="text-xs wt-muted mt-2 space-y-1">
          <li>• Animated Galaxy & Holo profile themes + premium channel themes</li>
          <li>• Premium badge, glowing avatar ring and boosted visibility in member search</li>
          <li>• +5 extra channel slots</li>
        </ul>
        {me.profile.premium ? (
          <div className="mt-3 text-sm text-[#39FF14] font-bold flex items-center gap-1" data-testid="walkie-premium-active"><Check className="w-4 h-4" /> Active until {new Date(me.profile.premium_until).toLocaleDateString()}</div>
        ) : null}
        <div className="flex gap-2 mt-3">
          <button onClick={() => pay(shop.premium.id)} disabled={!!busy} data-testid="walkie-premium-pay" className="flex-1 py-2.5 rounded-full bg-[#FF2BD6] text-black font-black text-sm">{me.profile.premium ? "Extend" : "Get"} {shop.premium.days} days · ${shop.premium.price.toFixed(2)}</button>
          <button onClick={premiumCoins} disabled={!!busy} data-testid="walkie-premium-coins" className="flex-1 py-2.5 rounded-full wt-card font-black text-sm">{shop.premium.coin_price} coins</button>
        </div>
        <div className="text-[10px] wt-muted mt-2">One-time 30-day pass — no automatic renewal.</div>
      </section>

      <section className="space-y-2">
        <h2 className="font-black">Coin extras</h2>
        {shop.items.map((it) => {
          const has = it.type !== "slot" && owned.has(it.id);
          return (
            <div key={it.id} className="wt-card rounded-xl p-3 flex items-center gap-3" data-testid={`walkie-item-${it.id}`}>
              <div className="flex-1"><div className="font-bold text-sm">{it.name}</div><div className="text-[10px] wt-muted uppercase">{it.type}</div></div>
              <button disabled={has || !!busy || me.coins < it.price} onClick={() => buyItem(it)} data-testid={`walkie-item-buy-${it.id}`}
                className="text-xs font-black px-3 py-1.5 rounded-full bg-[#FFE300] text-black disabled:bg-transparent disabled:border disabled:wt-line disabled:opacity-60">
                {has ? "Owned" : busy === it.id ? "…" : `${it.price} coins`}
              </button>
            </div>
          );
        })}
      </section>

      {wallet && (
        <section className="space-y-2" data-testid="walkie-history">
          <h2 className="font-black flex items-center gap-2"><History className="w-4 h-4" /> Transaction history</h2>
          {wallet.ledger.length === 0 && <div className="text-xs wt-muted">No transactions yet.</div>}
          {wallet.ledger.map((l) => (
            <div key={l.id} className="flex justify-between text-xs wt-card rounded-lg px-3 py-2">
              <span>{l.kind.replace("_", " ")} <span className="wt-muted">· {new Date(l.at).toLocaleString()}</span></span>
              <span className={l.delta >= 0 ? "text-[#39FF14] font-bold" : "text-[#FF003C] font-bold"}>{l.delta >= 0 ? "+" : ""}{l.delta}</span>
            </div>
          ))}
        </section>
      )}
      <p className="text-[11px] wt-muted flex gap-2"><ShieldCheck className="w-4 h-4 shrink-0 text-[#39FF14]" /> Secure checkout by Stripe. Coins are added only after our server verifies the payment. Basic voice chat is always free and ad-free.</p>
    </div>
  );
}
